import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LOGO_HEADER_INTRINSIC } from "@/lib/logoHeaderImage";
import { LogoHeaderImage } from "@/components/branding/LogoHeaderImage";
import { buildOrderNowSignupPath } from "@/lib/guestFirstOrder";
import { appendUtmToPath } from "@/lib/utmAttribution";

/**
 * Public marketing top bar: logo = Home (`/`); audience CTAs; Blog; Sign in; Sign up; Order now.
 */
export function MarketingHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const signinPath = appendUtmToPath("/auth/signin");
  const signupPath = appendUtmToPath("/auth/signup");

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === "/") {
      e.preventDefault();
      const home = document.getElementById("home");
      if (home) home.scrollIntoView({ behavior: "smooth" });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goOrderNow = () => {
    navigate(buildOrderNowSignupPath());
  };

  return (
    <header className="fixed top-0 w-full z-50 border-b border-border/40 bg-background/80 backdrop-blur-md">
      <div className="container mx-auto flex h-16 items-center justify-between gap-2 px-4 sm:px-6">
        <Link
          to="/"
          onClick={handleLogoClick}
          className="flex items-center shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          aria-label="Home"
        >
          {/* Fixed aspect box reserves space before the WebP decodes (CLS). */}
          <span
            className="block w-[140px] shrink-0 sm:w-[160px]"
            style={{ aspectRatio: `${LOGO_HEADER_INTRINSIC.width} / ${LOGO_HEADER_INTRINSIC.height}` }}
          >
            <LogoHeaderImage className="size-full max-h-8" />
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 lg:gap-8 flex-1 justify-end min-w-0">
          <Link
            to="/flight-attendants"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
          >
            Flight attendants
          </Link>
          <Link
            to="/for-caterers"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
          >
            Caterers
          </Link>
          <Link
            to="/blog"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
          >
            Blog
          </Link>
          <div className="flex items-center gap-2 shrink-0">
            <Button asChild variant="ghost" className="font-medium">
              <Link to={signinPath}>Sign in</Link>
            </Button>
            <Button asChild variant="outline" className="font-medium">
              <Link to={signupPath}>Sign up</Link>
            </Button>
            <Button onClick={goOrderNow} className="font-medium bg-accent hover:bg-accent/90">
              Order now
            </Button>
          </div>
        </nav>

        <div className="flex md:hidden items-center gap-1.5 shrink-0">
          <Button asChild variant="ghost" size="sm" className="font-medium px-2">
            <Link to={signinPath}>Sign in</Link>
          </Button>
          <Button asChild variant="outline" size="sm" className="font-medium px-2">
            <Link to={signupPath}>Sign up</Link>
          </Button>
          <Button onClick={goOrderNow} size="sm" className="font-medium bg-accent hover:bg-accent/90 px-2">
            Order now
          </Button>
        </div>
      </div>
    </header>
  );
}
